import _ from 'lodash';
import { AnyAction } from 'redux';
import IInvestmentSaleTransfer from '~Api/InvestmentSaleTransfer/IInvestmentSaleTransfer';
import { IDictionary } from '~utilities/IDictionary';
import InvestmentSaleTransfersActionsEnum from '~InvestmentSaleTransfers/ActionsEnum';
import {
    IInvestmentSaleTransferHistoriesSetAction,
    IInvestmentSaleTransferInvestorAccountInvestmentTransactionsSetAction,
    IInvestmentSaleTransferListSettingsSetAction,
    IInvestmentSaleTransferSetAction,
    IInvestmentSaleTransfersPaginatedSetAction,
    IInvestmentSaleTransfersUnallocatedInvestmentsSetAction,
} from './actions';
import IHistory from '~Api/InvestmentSaleTransfer/IHistory';
import IUnallocatedInvestment from '~Api/InvestmentSaleTransfer/IUnallocatedInvestment';
import { IInvestmentSaleTransferListSettings } from './List';
import ListSortFieldEnum from '~Api/InvestmentSaleTransfer/ListSortFieldEnum';

export interface IInvestmentSaleTransfersState {
    histories: IDictionary<IDictionary<IHistory>>;
    investmentSaleTransferInvestorAccountInvestmentTransactionUuids: IDictionary<string[]>;
    investmentSaleTransferListSettings: IInvestmentSaleTransferListSettings;
    investmentSaleTransfers: IDictionary<IInvestmentSaleTransfer>;
    investmentSaleTransfersPaginatedCount: number;
    investmentSaleTransfersPaginatedLoading: boolean;
    investmentSaleTransfersPaginatedUuids: string[];
    unallocatedInvestments: IDictionary<IUnallocatedInvestment>;
}

const initialData: IInvestmentSaleTransfersState = {
    histories: {},
    investmentSaleTransferInvestorAccountInvestmentTransactionUuids: {},
    investmentSaleTransferListSettings: {
        currentPage: 1,
        pageSize: 20,
        sortField: ListSortFieldEnum.CreatedTime,
        sortOrder: 'descend',
    },
    investmentSaleTransfers: null,
    investmentSaleTransfersPaginatedCount: null,
    investmentSaleTransfersPaginatedLoading: false,
    investmentSaleTransfersPaginatedUuids: null,
    unallocatedInvestments: null,
};

function dehydrateInvestmentSaleTransfer(investmentSaleTransfer: IInvestmentSaleTransfer): IInvestmentSaleTransfer {
    return _.omit(investmentSaleTransfer, [ 'investment' ]);
}

function dehydratUnallocatedInvestment(unallocatedInvestment: IUnallocatedInvestment): IUnallocatedInvestment {
    return _.omit(unallocatedInvestment, [ 'investment' ]);
}

export function investmentSaleTransfersReducer(state: IInvestmentSaleTransfersState = initialData, action: AnyAction): IInvestmentSaleTransfersState {
    switch (action.type) {
        case InvestmentSaleTransfersActionsEnum.InvestmentSaleTransferSet: {
            const typedAction: IInvestmentSaleTransferSetAction = action as IInvestmentSaleTransferSetAction;

            return {
                ...state,
                investmentSaleTransfers: {
                    ...state.investmentSaleTransfers,
                    [typedAction.investmentSaleTransfer.uuid]: dehydrateInvestmentSaleTransfer(typedAction.investmentSaleTransfer),
                },
            };
        }

        case InvestmentSaleTransfersActionsEnum.InvestmentSaleTransferHistoriesSet: {
            const typedAction: IInvestmentSaleTransferHistoriesSetAction = action as IInvestmentSaleTransferHistoriesSetAction;

            return {
                ...state,
                histories: {
                    ...state.histories,
                    [typedAction.investmentSaleTransferUuid]: _.keyBy(typedAction.histories, 'uuid'),
                },
            };
        }

        case InvestmentSaleTransfersActionsEnum.InvestmentSaleTransferInvestorAccountInvestmentTransactionsSet: {
            const typedAction: IInvestmentSaleTransferInvestorAccountInvestmentTransactionsSetAction = action as IInvestmentSaleTransferInvestorAccountInvestmentTransactionsSetAction;

            return {
                ...state,
                investmentSaleTransferInvestorAccountInvestmentTransactionUuids: {
                    ...state.investmentSaleTransferInvestorAccountInvestmentTransactionUuids,
                    [typedAction.investmentSaleTransferUuid]: _.map(typedAction.investorAccountInvestmentTransactions, 'uuid'),
                },
            };
        }

        case InvestmentSaleTransfersActionsEnum.InvestmentSaleTransfersUnallocatedInvestmentsSet: {
            const typedAction: IInvestmentSaleTransfersUnallocatedInvestmentsSetAction = action as IInvestmentSaleTransfersUnallocatedInvestmentsSetAction;

            const unallocatedInvestments: IDictionary<IUnallocatedInvestment> = {};

            _.each(typedAction.unallocatedInvestments, (unallocatedInvestment: IUnallocatedInvestment) => {
                unallocatedInvestments[unallocatedInvestment.investmentUuid] = dehydratUnallocatedInvestment(unallocatedInvestment);
            });

            return {
                ...state,
                unallocatedInvestments,
            };
        }

        case InvestmentSaleTransfersActionsEnum.InvestmentSaleTransfersPaginatedList: {
            return {
                ...state,
                investmentSaleTransfersPaginatedLoading: true,
                investmentSaleTransfersPaginatedUuids: null,
            };
        }

        case InvestmentSaleTransfersActionsEnum.InvestmentSaleTransfersPaginatedSet: {
            const typedAction: IInvestmentSaleTransfersPaginatedSetAction = action as IInvestmentSaleTransfersPaginatedSetAction;

            const investmentSaleTransfers: IDictionary<IInvestmentSaleTransfer> = {};
            const investmentSaleTransfersPaginatedUuids: string[] = [];

            _.each(typedAction.investmentSaleTransfers, (investmentSaleTransfer: IInvestmentSaleTransfer): void => {
                investmentSaleTransfers[investmentSaleTransfer.uuid] = dehydrateInvestmentSaleTransfer(investmentSaleTransfer);
                investmentSaleTransfersPaginatedUuids.push(investmentSaleTransfer.uuid);
            });

            return {
                ...state,
                investmentSaleTransfers: {
                    ...state.investmentSaleTransfers,
                    ...investmentSaleTransfers,
                },
                investmentSaleTransfersPaginatedCount: typedAction.investmentSaleTransfersPaginatedCount,
                investmentSaleTransfersPaginatedLoading: false,
                investmentSaleTransfersPaginatedUuids,
            };
        }

        case InvestmentSaleTransfersActionsEnum.InvestmentSaleTransferListSettingsSet: {
            const typedAction: IInvestmentSaleTransferListSettingsSetAction = action as IInvestmentSaleTransferListSettingsSetAction;

            return {
                ...state,
                investmentSaleTransferListSettings: typedAction.settings,
            };
        }

        default: {
            return state;
        }
    }
}
